import * as THREE from 'three';

export class TrailEffect {
  private points: THREE.Vector3[] = [];
  private maxPoints: number;
  private width: number;
  private color: THREE.Color;
  private opacity: number;
  private fadeRate: number;
  
  public geometry: THREE.BufferGeometry;
  public material: THREE.ShaderMaterial;
  public mesh: THREE.Mesh;

  constructor(maxPoints: number = 30, width: number = 0.5) {
    this.maxPoints = maxPoints;
    this.width = width;
    this.color = new THREE.Color(0x44ff88);
    this.opacity = 1.0;
    this.fadeRate = 0.95;
    
    this.geometry = new THREE.BufferGeometry();
    this.initializeGeometry();
    
    this.material = this.createTrailMaterial();
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
  }

  private initializeGeometry(): void {
    const positions = new Float32Array(this.maxPoints * 3);
    const alphas = new Float32Array(this.maxPoints);
    
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('alpha', new THREE.BufferAttribute(alphas, 1));
  }

  private createTrailMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      vertexShader: `
        attribute float alpha;
        varying float vAlpha;
        
        void main() {
          vAlpha = alpha;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          gl_PointSize = ${this.width.toFixed(1)} * (300.0 / -mvPosition.z);
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        varying float vAlpha;
        
        void main() {
          float dist = length(gl_PointCoord - vec2(0.5));
          if (dist > 0.5) discard;
          
          float alpha = vAlpha * (1.0 - dist * 2.0);
          gl_FragColor = vec4(color, alpha);
        }
      `,
      uniforms: {
        color: { value: this.color }
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
  }

  public addPoint(position: THREE.Vector3): void {
    this.points.unshift(position.clone());
    
    if (this.points.length > this.maxPoints) {
      this.points.pop();
    }
    
    this.updateGeometry();
  }

  private updateGeometry(): void {
    const positions = this.geometry.attributes['position'] as THREE.BufferAttribute;
    const alphas = this.geometry.attributes['alpha'] as THREE.BufferAttribute;
    
    for (let i = 0; i < this.maxPoints; i++) {
      if (i < this.points.length) {
        const point = this.points[i];
        positions.setXYZ(i, point.x, point.y, point.z);
        
        // Fade based on position in trail
        const alpha = Math.pow(1.0 - (i / this.maxPoints), 2);
        alphas.setX(i, alpha * this.opacity);
      } else {
        positions.setXYZ(i, 0, 0, 0);
        alphas.setX(i, 0);
      }
    }
    
    positions.needsUpdate = true;
    alphas.needsUpdate = true;
  }

  public update(_deltaTime: number): void {
    // Fade trail over time
    this.opacity *= this.fadeRate;
    
    if (this.opacity < 0.01) {
      this.clear();
    } else {
      this.updateGeometry();
    }
  }

  public clear(): void {
    this.points = [];
    this.opacity = 1.0;
    this.updateGeometry();
  }

  public setMaxPoints(maxPoints: number): void {
    this.maxPoints = maxPoints;
    this.initializeGeometry();
  }

  public setColor(color: THREE.Color): void {
    this.color.copy(color);
    this.material.uniforms['color'].value.copy(color);
  }

  public setWidth(width: number): void {
    this.width = width;
    this.material = this.createTrailMaterial();
    this.mesh.material = this.material;
  }

  public dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    this.points = [];
  }
}

/**
 * Phase 3: Tractor Beam Effect for Mining Selection
 * Creates visual beam connecting mining collector to targeted transaction ship
 */
export class TractorBeam {
  private geometry: THREE.BufferGeometry;
  private material: THREE.LineBasicMaterial;
  private line: THREE.Line;
  private isActive: boolean = false;
  private startPosition: THREE.Vector3;
  private endPosition: THREE.Vector3;
  private intensity: number = 0;
  private targetIntensity: number = 0;
  private animationTime: number = 0;
  private maxIntensity: number = 1.0;
  private beamWidth: number = 0.5;
  private pulseSpeed: number = 3.0;

  constructor() {
    this.startPosition = new THREE.Vector3();
    this.endPosition = new THREE.Vector3();
    
    // Create beam geometry
    this.geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(6); // Two points, 3 components each
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    // Create beam material with animated properties
    this.material = new THREE.LineBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0,
      linewidth: this.beamWidth
    });
    
    this.line = new THREE.Line(this.geometry, this.material);
    this.line.visible = false;
  }

  public activate(
    startPos: THREE.Vector3, 
    endPos: THREE.Vector3, 
    miningPower: number = 1.0
  ): void {
    this.isActive = true;
    this.startPosition.copy(startPos);
    this.endPosition.copy(endPos);
    this.targetIntensity = Math.min(miningPower, this.maxIntensity);
    this.line.visible = true;
    
    // Update geometry
    this.updateGeometry();
  }

  public deactivate(): void {
    this.isActive = false;
    this.targetIntensity = 0;
    this.intensity = 0;
    this.line.visible = false;
  }

  public updatePositions(startPos: THREE.Vector3, endPos: THREE.Vector3): void {
    this.startPosition.copy(startPos);
    this.endPosition.copy(endPos);
    this.updateGeometry();
  }

  public update(deltaTime: number): void {
    if (!this.isActive && this.intensity <= 0) return;

    this.animationTime += deltaTime;

    // Smoothly transition intensity
    const intensitySpeed = 2.0;
    if (this.intensity < this.targetIntensity) {
      this.intensity = Math.min(this.targetIntensity, this.intensity + intensitySpeed * deltaTime);
    } else if (this.intensity > this.targetIntensity) {
      this.intensity = Math.max(this.targetIntensity, this.intensity - intensitySpeed * deltaTime);
    }

    // Hide beam when intensity reaches zero
    if (this.intensity <= 0) {
      this.line.visible = false;
      return;
    }

    // Pulsing effect
    const pulseValue = 0.5 + 0.5 * Math.sin(this.animationTime * this.pulseSpeed);
    const finalIntensity = this.intensity * (0.7 + 0.3 * pulseValue);

    // Update material properties
    this.material.opacity = finalIntensity;
    this.material.color.setHSL(0.5, 1.0, 0.5 + 0.3 * pulseValue); // Cyan with brightness variation
  }

  private updateGeometry(): void {
    const positions = this.geometry.attributes['position'].array as Float32Array;
    
    positions[0] = this.startPosition.x;
    positions[1] = this.startPosition.y;
    positions[2] = this.startPosition.z;
    
    positions[3] = this.endPosition.x;
    positions[4] = this.endPosition.y;
    positions[5] = this.endPosition.z;
    
    this.geometry.attributes['position'].needsUpdate = true;
  }

  public getMesh(): THREE.Line {
    return this.line;
  }

  public getIntensity(): number {
    return this.intensity;
  }

  public isBeamActive(): boolean {
    return this.isActive && this.intensity > 0;
  }

  public dispose(): void {
    if (this.geometry) {
      this.geometry.dispose();
    }
    if (this.material) {
      this.material.dispose();
    }
  }
}
